import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, ImageIcon, MessageSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage, saveBlob } from '@/lib/api/client';
import type { GiftCardBatchDetail } from '@/lib/api/types';
import { dateTime, humanize, money, num, phone as phoneFmt } from '@/lib/format';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Checkbox, Field, Input, Textarea } from '@/ui/Field';
import { giftCardTone } from '@/ui/GiftCardDetail';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';

export const Route = createFileRoute('/_app/gift-cards_/batches_/$id')({
  staticData: { crumb: 'Batch' },
  component: BatchScreen,
});

function BatchScreen() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const batchId = Number(id);

  const batchQuery = useQuery({
    queryKey: ['gift-card-batches', batchId],
    queryFn: () => api.get<GiftCardBatchDetail>(`/gift-card-batches/${batchId}`),
  });

  const download = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.download(
        `/gift-card-batches/${batchId}/download-all`
      );
      saveBlob(
        blob,
        filename.endsWith('.zip') ? filename : `gift-card-batch-${batchId}.zip`
      );
    },
    onSuccess: () => toast.success('Card images downloaded.'),
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (batchQuery.isError) {
    return (
      <Page>
        <PageHeader eyebrow="Gift cards" title="Batch" />
        <PageBody>
          <ErrorState
            title="Could not load this batch"
            message={errorMessage(batchQuery.error)}
            onRetry={() => void batchQuery.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  const batch = batchQuery.data;
  if (!batch) {
    return (
      <Page>
        <PageHeader eyebrow="Gift cards" title="Batch" />
        <PageBody>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Gift cards"
        title={batch.name}
        subtitle={`${num(batch.quantity)} cards · created ${dateTime(batch.createdAt)}`}
        actions={
          <>
            <Button
              asChild
              variant="ghost"
              size="sm"
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link to="/gift-cards/batches" search={{ page: 1 }}>
                Batches
              </Link>
            </Button>
            <Button
              size="sm"
              icon={<Download className="size-3.5" />}
              loading={download.isPending}
              onClick={() => download.mutate()}
              title="Download every card image as a ZIP"
            >
              Download all
            </Button>
          </>
        }
      />
      <PageBody>
        <div className="flex flex-col gap-3">
          <StatRow>
            <Stat label="Cards" value={num(batch.quantity)} />
            <Stat label="Face value each" value={money(batch.faceValueCents)} />
            <Stat label="Total face value" value={money(batch.totalFaceValueCents)} />
            <Stat
              label="Charged"
              value={money(batch.chargedAmountCents)}
              sub={
                batch.chargeMethod ? humanize(batch.chargeMethod) : 'no charge recorded'
              }
              tone={batch.chargeMethod === 'house_account' ? 'warn' : undefined}
            />
          </StatRow>

          <div className="grid gap-3 xl:grid-cols-2">
            <DesignPanel batch={batch} />
            <div className="flex min-w-0 flex-col gap-3">
              <Panel title="Charge">
                <DetailList columns={2}>
                  <Detail label="Method">
                    {batch.chargeMethod ? humanize(batch.chargeMethod) : 'None'}
                  </Detail>
                  <Detail label="Discount" mono>
                    {batch.discountCents ? `−${money(batch.discountCents)}` : '—'}
                  </Detail>
                  <Detail label="Charged to">
                    {batch.chargeCustomerId ? (
                      <Link
                        to="/customers/$id"
                        params={{ id: String(batch.chargeCustomerId) }}
                        search={{ page: 1 }}
                        className="text-signal-ink hover:underline"
                      >
                        Customer #{batch.chargeCustomerId}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </Detail>
                  <Detail label="Amount" mono>
                    {money(batch.chargedAmountCents)}
                  </Detail>
                </DetailList>
                {batch.notes ? (
                  <p className="text-ink-muted mt-3 text-xs leading-relaxed whitespace-pre-wrap">
                    {batch.notes}
                  </p>
                ) : null}
              </Panel>

              <WhatsAppPanel batch={batch} />
            </div>
          </div>

          <Panel
            title="Cards"
            description={`${num(batch.cards.length)} in this batch`}
            bleed
          >
            {batch.cards.length === 0 ? (
              <EmptyState compact title="No cards in this batch" />
            ) : (
              <div className="divide-line-soft divide-y">
                <div
                  className="border-line bg-surface-sunken text-2xs text-ink-faint grid items-center gap-x-3 border-b px-3 font-medium tracking-wider uppercase"
                  style={{
                    gridTemplateColumns: '9rem minmax(0,1fr) 7rem 6rem 9rem',
                    height: '1.75rem',
                  }}
                >
                  <div>Code</div>
                  <div>Name on card</div>
                  <div className="text-right">Balance</div>
                  <div>Status</div>
                  <div>Recipient</div>
                </div>
                {batch.cards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() =>
                      void navigate({
                        to: '/gift-cards/$id',
                        params: { id: String(card.id) },
                      })
                    }
                    className="hover:bg-surface-hover grid w-full items-center gap-x-3 px-3 py-1.5 text-left text-xs"
                    style={{ gridTemplateColumns: '9rem minmax(0,1fr) 7rem 6rem 9rem' }}
                  >
                    <Code>{card.code}</Code>
                    <span className="text-ink truncate">{card.label || '—'}</span>
                    <span className="text-ink text-right font-mono tabular-nums">
                      {money(card.balanceCents)}
                    </span>
                    <span>
                      <Badge tone={giftCardTone(card.status)}>{card.status}</Badge>
                    </span>
                    <span className="text-ink-muted truncate font-mono tabular-nums">
                      {card.recipientPhone ? phoneFmt(card.recipientPhone) : '—'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </PageBody>
    </Page>
  );
}

/* ── Card design ──────────────────────────────────────────────────────────────
   The preview is a live PNG rendered by the API, so it is an <img> pointed at
   the endpoint with a cache-buster that changes every time the design does. */

function DesignPanel({ batch }: { batch: GiftCardBatchDetail }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState(batch.message ?? '');
  const [instructions, setInstructions] = useState(batch.designInstructions ?? '');
  const [showName, setShowName] = useState(!!batch.showName);
  const [logo, setLogo] = useState<File | null>(null);
  const [bust, setBust] = useState(() => Date.now());

  const regenerate = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (logo) form.append('logo', logo);
      form.append('message', message);
      form.append('designInstructions', instructions);
      form.append('showName', String(showName));
      // Image generation calls out to an AI service — give it real time.
      return api.post<{ backgroundGenerated: boolean; logoUploaded: boolean }>(
        `/gift-card-batches/${batch.id}/generate-background`,
        form,
        { timeout: 180_000 }
      );
    },
    onSuccess: (result) => {
      setBust(Date.now());
      setLogo(null);
      void qc.invalidateQueries({ queryKey: ['gift-card-batches', batch.id] });
      toast.success(
        result.backgroundGenerated
          ? 'Design regenerated.'
          : 'Design saved (background unchanged).'
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Panel
      title="Card design"
      description="Shared by every card in the batch"
      actions={
        <Button
          variant="primary"
          size="xs"
          icon={<Sparkles className="size-3.5" />}
          loading={regenerate.isPending}
          onClick={() => regenerate.mutate()}
        >
          Regenerate
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="border-line bg-surface-sunken overflow-hidden rounded border">
          <img
            src={`/api/gift-card-batches/${batch.id}/preview?v=${bust}`}
            alt={`Sample card for ${batch.name}`}
            className="block h-auto w-full"
          />
        </div>

        <Field label="Printed message" hint="Appears on every card.">
          <Textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
          />
        </Field>
        <Field
          label="Design instructions"
          hint="Describes the background the generator should draw."
        >
          <Textarea
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.currentTarget.value)}
            placeholder="e.g. warm olive tones, subtle produce pattern, lots of empty space on the right"
          />
        </Field>
        <Field
          label="Logo"
          hint="PNG or JPG. Optional — the previous logo is kept if empty."
        >
          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept="image/*"
              className="file:bg-surface-active file:text-2xs file:text-ink file:mr-2 file:rounded file:border-0 file:px-2 file:py-0.5"
              onChange={(e) => setLogo(e.currentTarget.files?.[0] ?? null)}
            />
            {batch.logoPath ? (
              <span className="text-2xs text-ink-faint shrink-0">
                <ImageIcon className="mr-1 inline size-3" />
                on file
              </span>
            ) : null}
          </div>
        </Field>
        <Checkbox
          checked={showName}
          onCheckedChange={setShowName}
          label="Print the card name on each card"
        />
        <p className="text-2xs text-ink-faint leading-relaxed">
          Regenerating redraws the background and re-saves the message, instructions and
          name setting. It can take a minute.
        </p>
      </div>
    </Panel>
  );
}

function WhatsAppPanel({ batch }: { batch: GiftCardBatchDetail }) {
  const qc = useQueryClient();
  const [text, setText] = useState(batch.whatsappMessage ?? '');
  const dirty = text !== (batch.whatsappMessage ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ updated: boolean }>(`/gift-card-batches/${batch.id}/whatsapp`, {
        whatsappMessage: text,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gift-card-batches', batch.id] });
      toast.success('Caption saved.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Panel
      title="WhatsApp caption"
      description="Sent with each card image"
      actions={
        dirty ? (
          <Button
            variant="primary"
            size="xs"
            icon={<MessageSquare className="size-3.5" />}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        ) : null
      }
    >
      <Textarea
        rows={4}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        placeholder="Hi {name}, here is your gift card…"
      />
      <p className="text-2xs text-ink-faint mt-2 leading-relaxed">
        Sending happens from the register-side WhatsApp session; this only stores the
        caption template.
      </p>
    </Panel>
  );
}
